import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { SORT_NEWEST } from '../../config/configSearch';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { PARTNER_USER_FIELDS } from '../../util/userTypes';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  getProcess,
  isBookingProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': [
          'variants.square-xsmall',
          'variants.square-xsmall2x',
          'variants.square-small',
          'variants.square-small2x',
        ],
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

///////////////////////////
// Fetch Seller Reviews  //
///////////////////////////
// Reviews *about the seller* (subject = author), used to show the
// seller's average rating on the SellerCard. Distinct from
// fetchReviews above, which fetches reviews of this specific listing.
// Phase 1: aggregated on the fly — see src/util/reviews.js for the
// migration note on caching the average on the user later.
export const fetchSellerReviewsThunk = createAsyncThunk(
  'ListingPage/fetchSellerReviews',
  ({ sellerId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        subject_id: sellerId,
        state: 'public',
        // Only rating + type needed for the average; skip author/image
        // includes to keep this lightweight.
        'fields.review': ['rating', 'type', 'state'],
        per_page: 100,
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchSellerReviews = sellerId => (dispatch, getState, sdk) => {
  return dispatch(fetchSellerReviewsThunk({ sellerId })).unwrap();
};

////////////////////////////////
// Fetch similar listings    //
// (Lignende plagg strip)    //
////////////////////////////////

// Two-pass candidate pool, NARROW FIRST.
//
// This used to be a single `pub_categoryLevel1` query (i.e. all of "Klær")
// sorted by createdAt. That starved the reranker: for a skirt with 12
// in-stock siblings, only ONE of them was inside the newest-40 clothing
// window, so the strip could not have shown skirts even in principle —
// which is exactly what the client reported ("carousel headline is
// Lignende, but it shows tops and dresses").
//
// So we ask for the same garment type explicitly, and keep a smaller
// same-Klær pool purely as filler for thin subcategories. The two run in
// parallel — an extra concurrent request rather than an extra round trip —
// and the component reranks the merged pool (see SimilarListings.js).
const SIMILAR_PER_PAGE = 40;
// Filler only: used to top up when a subcategory is genuinely sparse, so
// the strip degrades to "same category" rather than rendering empty.
const SIMILAR_FALLBACK_PER_PAGE = 20;

const fetchSimilarListingsPayloadCreator = (arg, { extra: sdk, dispatch, rejectWithValue }) => {
  const { listingId, categoryLevel1, categoryLevel2, listingImageConfig } = arg;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const baseParams = {
    page: 1,
    sort: SORT_NEWEST,
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['images', 'author', 'author.profileImage'],
    'fields.listing': [
      'title',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.merke',
      'publicData.merke-annet',
      'publicData.tilstand',
      'publicData.storrelse-klar',
      'publicData.storrelse-sko',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
    ],
    // Include publicData so the vanity `username` survives here: this
    // response's author entities get merged into marketplaceData and
    // that merge OVERWRITES the author resource (see the image-variants
    // note below). Without publicData, the seller's username — loaded by
    // the main showListing fetch — would be clobbered, and profile links
    // (SellerCard, UserCard) would fall back to the raw UUID.
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      ...PARTNER_USER_FIELDS,
    ],
    'fields.image': [
      // Carousel-size scaled variants for the main listing's gallery.
      // The similar-listings response merges image entities into
      // marketplaceData via `included`, and that merge OVERWRITES
      // existing variants (rather than deep-merging them). Without
      // these variants here, any image entity touched by this
      // response would lose the scaled-medium/large/xlarge URLs the
      // showListing fetch populated — and the ListingPage carousel,
      // which renders at scaled-* sizes, would silently fall back to
      // scaled-small. Listing IDs are filtered in `then()` below but
      // image IDs in `response.data.included` aren't, so we have to
      // request the same set here.
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Listing-card crops actually used by the similar-listings UI.
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media variants — the showListing fetch requests these
      // too; same overwrite reasoning as the scaled variants above.
      'variants.facebook',
      'variants.twitter',

      // Avatar variants — the similar-listings response re-merges the
      // listing's author into marketplaceData, so it must carry the
      // same profileImage variants the main fetch did, otherwise the
      // SellerCard avatar's srcset goes empty after this resolves.
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    'limit.images': 1,
  };

  // Same garment type first; same top-level category as filler.
  const narrowQuery = categoryLevel2
    ? sdk.listings.query({
        ...baseParams,
        perPage: SIMILAR_PER_PAGE,
        pub_categoryLevel2: categoryLevel2,
      })
    : Promise.resolve(null);
  const broadQuery = categoryLevel1
    ? sdk.listings.query({
        ...baseParams,
        perPage: SIMILAR_FALLBACK_PER_PAGE,
        pub_categoryLevel1: categoryLevel1,
      })
    : Promise.resolve(null);

  return Promise.all([narrowQuery, broadQuery])
    .then(([narrowResponse, broadResponse]) => {
      const primary = narrowResponse || broadResponse;
      if (!primary) {
        return { listingIds: [] };
      }
      // Merge narrow-first so same-type items lead, then dedupe. `included`
      // is concatenated too — the entities merged into marketplaceData must
      // cover both result sets or cards lose their images/authors.
      const seenListing = new Set();
      const mergedListings = [narrowResponse, broadResponse]
        .filter(Boolean)
        .flatMap(r => r.data.data)
        .filter(l => {
          const id = l.id?.uuid;
          if (!id || seenListing.has(id)) {
            return false;
          }
          seenListing.add(id);
          return true;
        });
      const seenIncluded = new Set();
      const mergedIncluded = [narrowResponse, broadResponse]
        .filter(Boolean)
        .flatMap(r => r.data.included || [])
        .filter(entity => {
          const key = `${entity.type}:${entity.id?.uuid}`;
          if (seenIncluded.has(key)) {
            return false;
          }
          seenIncluded.add(key);
          return true;
        });
      const response = {
        ...primary,
        data: { ...primary.data, data: mergedListings, included: mergedIncluded },
      };
      return response;
    })
    .then(response => {
      if (!response.data) {
        return response;
      }
      // Drop the current listing from the response BEFORE merging into
      // marketplaceData. The similar-listings query uses a slimmed-down
      // `fields.listing` (no description, location etc.), so if the
      // current listing comes back here too — which it can, since the
      // category filter matches itself — the merge would clobber the
      // full entity already populated by showListing(). Image entities
      // in `response.data.included` can't be filtered the same way, so
      // the variant list above mirrors showListing's to keep the merge
      // a no-op rather than an overwrite.
      const filteredData = response.data.data.filter(l => l.id?.uuid !== listingId);
      const filteredResponse = {
        ...response,
        data: { ...response.data, data: filteredData },
      };
      dispatch(addMarketplaceEntities(filteredResponse));
      // Store the SDK UUID objects (not the bare `.uuid` strings) so
      // `getListingsById` — which reads `id.uuid` on each resource —
      // can resolve them back to entities. Storing strings here is
      // what was making the similar-listings section silently empty.
      const ids = filteredData.map(l => l.id).filter(Boolean);
      return { listingIds: ids };
    })
    .catch(e => {
      log.error(e, 'fetch-similar-listings-failed', { listingId });
      return rejectWithValue(storableError(e));
    });
};

export const fetchSimilarListingsThunk = createAsyncThunk(
  'ListingPage/fetchSimilarListings',
  fetchSimilarListingsPayloadCreator
);

export const fetchSimilarListings = (
  listingId,
  categoryLevel1,
  categoryLevel2,
  listingImageConfig
) => dispatch =>
  dispatch(
    fetchSimilarListingsThunk({ listingId, categoryLevel1, categoryLevel2, listingImageConfig })
  );

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');

  // Negotiation process does not support inquiry for customer role on OFFER flog (only request for quote is supported)
  const isNegotiationProcess = isNegotiationProcessAlias(processAlias);
  const unitType = listing?.attributes?.publicData?.unitType || '';
  if (isNegotiationProcess && unitType === OFFER) {
    return rejectWithValue(
      storableError(
        new Error(
          'Negotiation process with unit type OFFER does not support inquiry for customer role'
        )
      )
    );
  }

  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(setCurrentUserHasOrders());
        return transactionId;
      });
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  sellerReviews: [],
  fetchSellerReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  // Lignende plagg ("Similar items") strip — populated from loadData.
  // Stores ids only; the entities live in marketplaceData.duck.
  similarListingIds: [],
  fetchSimilarListingsError: null,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
        state.id = action.payload.data.data.id;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchSellerReviewsThunk.pending, state => {
        state.fetchSellerReviewsError = null;
      })
      .addCase(fetchSellerReviewsThunk.fulfilled, (state, action) => {
        state.sellerReviews = action.payload;
      })
      .addCase(fetchSellerReviewsThunk.rejected, (state, action) => {
        state.fetchSellerReviewsError = action.payload;
      })
      .addCase(fetchSimilarListingsThunk.pending, state => {
        state.fetchSimilarListingsError = null;
      })
      .addCase(fetchSimilarListingsThunk.fulfilled, (state, action) => {
        state.similarListingIds = action.payload.listingIds;
      })
      .addCase(fetchSimilarListingsThunk.rejected, (state, action) => {
        state.fetchSimilarListingsError = action.payload;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues } = listingPageSlice.actions;

export default listingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const promises = hasNoViewingRights
    ? // If user has no viewing rights, only allow fetching their own listing without reviews
      [dispatch(showListing(listingId, config, true))]
    : // For users with viewing rights, fetch the listing and the associated reviews
      [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  return Promise.all(promises).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    // Kick off the "Lignende plagg" strip so it's ready to render
    // alongside the main content. Fire-and-forget — a failure here
    // shouldn't block the page (the section just won't render).
    if (listing?.id?.uuid && !hasNoViewingRights) {
      const similarPublicData = listing.attributes?.publicData || {};
      dispatch(
        fetchSimilarListings(
          listing.id.uuid,
          similarPublicData.categoryLevel1 || null,
          similarPublicData.categoryLevel2 || null,
          config?.layout?.listingImage
        )
      );
    }

    // Seller's average rating for the SellerCard. Fire-and-forget — the
    // card just hides the rating row if this fails or returns nothing.
    const sellerId = listing?.relationships?.author?.data?.id;
    if (sellerId && !hasNoViewingRights) {
      dispatch(fetchSellerReviews(sellerId));
    }

    return response;
  });
};
